import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  LinearProgress, Chip, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import millingApi from '../../../services/millingApi';

const SiloDashboard: React.FC = () => {
  const [silos, setSilos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; silo?: any }>({ open: false });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ siloCode: '', siloName: '', capacityMt: '', location: '', notes: '' });

  const load = async () => {
    try {
      setLoading(true);
      const data = await millingApi.getSilos();
      setSilos(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load silos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ siloCode: '', siloName: '', capacityMt: '', location: '', notes: '' });
    setDialog({ open: true });
  };

  const openEdit = (silo: any) => {
    setForm({
      siloCode: silo.siloCode,
      siloName: silo.siloName,
      capacityMt: String(silo.capacityMt),
      location: silo.location || '',
      notes: silo.notes || '',
    });
    setDialog({ open: true, silo });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        siloCode: form.siloCode,
        siloName: form.siloName,
        capacityMt: Number(form.capacityMt),
        location: form.location || undefined,
        notes: form.notes || undefined,
      };
      if (dialog.silo) {
        await millingApi.updateSilo(dialog.silo.id, payload);
      } else {
        await millingApi.createSilo(payload);
      }
      setDialog({ open: false });
      load();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this silo?')) return;
    await millingApi.deleteSilo(id);
    load();
  };

  const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    const map: Record<string, any> = { active: 'success', empty: 'default', full: 'warning', maintenance: 'error', inactive: 'default' };
    return map[status] || 'default';
  };

  const fillPct = (silo: any) => {
    if (!silo.capacityMt || Number(silo.capacityMt) === 0) return 0;
    return Math.min((Number(silo.currentStockMt) / Number(silo.capacityMt)) * 100, 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Silo Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={load}><Refresh /></IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Silo</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : silos.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="textSecondary">No silos configured. Add your first silo.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {silos.map((silo: any) => {
            const pct = fillPct(silo);
            const barColor = pct > 90 ? 'error' : pct > 70 ? 'warning' : 'success';
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={silo.id}>
                <Card sx={{ height: '100%', position: 'relative' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold">{silo.siloCode}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => openEdit(silo)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(silo.id)}><Delete fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>{silo.siloName}</Typography>
                    {silo.location && <Typography variant="caption" color="textSecondary" display="block">{silo.location}</Typography>}

                    <Box sx={{ my: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">{Number(silo.currentStockMt).toFixed(1)} MT</Typography>
                        <Typography variant="caption">{Number(silo.capacityMt).toFixed(1)} MT cap</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        color={barColor}
                        sx={{ height: 12, borderRadius: 6 }}
                      />
                      <Typography variant="caption" color="textSecondary">{pct.toFixed(1)}% full</Typography>
                    </Box>

                    <Chip label={silo.status} color={statusColor(silo.status)} size="small" />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.silo ? 'Edit Silo' : 'Add Silo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField label="Silo Code *" value={form.siloCode} onChange={(e) => setForm(f => ({ ...f, siloCode: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Silo Name *" value={form.siloName} onChange={(e) => setForm(f => ({ ...f, siloName: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Capacity (MT) *" type="number" value={form.capacityMt} onChange={(e) => setForm(f => ({ ...f, capacityMt: e.target.value }))} fullWidth inputProps={{ min: 0, step: 0.1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Location" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.siloCode || !form.siloName || !form.capacityMt}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiloDashboard;
